import { useEffect, type RefObject } from 'react';

const REGION_GUARD_ID = 'mm-forced-dark-region-guard';
const REGION_LOCK_CLASS = 'mm-forced-dark-region-lock';
const REGION_COUNTER_CLASS = 'mm-forced-dark-region-lock--counter-invert';

function documentLooksForceInverted(): boolean {
  const html = document.documentElement;
  const body = document.body;
  const htmlFilter = `${html.getAttribute('style') || ''} ${getComputedStyle(html).filter || ''}`;
  const bodyFilter = body
    ? `${body.getAttribute('style') || ''} ${getComputedStyle(body).filter || ''}`
    : '';
  return `${htmlFilter} ${bodyFilter}`.toLowerCase().includes('invert');
}

function ensureRegionGuardStylesheet(): void {
  let style = document.getElementById(REGION_GUARD_ID) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement('style');
    style.id = REGION_GUARD_ID;
    document.head.appendChild(style);
  }

  style.textContent = `
.${REGION_LOCK_CLASS} {
  color-scheme: only light !important;
  forced-color-adjust: none !important;
  -webkit-forced-color-adjust: none !important;
  background-color: #ffffff !important;
  color: #111827 !important;
  filter: none !important;
  -webkit-filter: none !important;
}
.${REGION_LOCK_CLASS}.${REGION_COUNTER_CLASS} {
  filter: invert(1) hue-rotate(180deg) !important;
  -webkit-filter: invert(1) hue-rotate(180deg) !important;
}
.${REGION_LOCK_CLASS} svg {
  background-color: #ffffff !important;
}
`;
}

function stripDarkReaderInlineFromSubtree(root: HTMLElement): void {
  root.querySelectorAll('*').forEach((el) => {
    const htmlEl = el as HTMLElement;
    const attrs = htmlEl.attributes;
    for (let i = attrs.length - 1; i >= 0; i -= 1) {
      const attr = attrs.item(i);
      if (attr && attr.name.startsWith('data-darkreader')) {
        htmlEl.removeAttribute(attr.name);
      }
    }
    if (htmlEl.style) {
      htmlEl.style.removeProperty('--darkreader-inline-bgcolor');
      htmlEl.style.removeProperty('--darkreader-inline-color');
      htmlEl.style.removeProperty('--darkreader-inline-bgimage');
      htmlEl.style.removeProperty('--darkreader-inline-border');
      htmlEl.style.removeProperty('--darkreader-inline-outline');
      htmlEl.style.removeProperty('--darkreader-inline-boxshadow');
    }
  });
}

function applyRegionLock(root: HTMLElement): void {
  root.classList.add(REGION_LOCK_CLASS);
  root.setAttribute('data-darkreader-ignore', 'true');
  root.setAttribute('data-color-scheme', 'light');
  root.style.setProperty('color-scheme', 'only light', 'important');
  root.style.setProperty('background-color', '#ffffff', 'important');

  const inverted = documentLooksForceInverted();
  root.classList.toggle(REGION_COUNTER_CLASS, inverted);

  stripDarkReaderInlineFromSubtree(root);
}

function clearRegionLock(root: HTMLElement): void {
  root.classList.remove(REGION_LOCK_CLASS, REGION_COUNTER_CLASS);
  root.removeAttribute('data-darkreader-ignore');
  root.removeAttribute('data-color-scheme');
  root.style.removeProperty('color-scheme');
  root.style.removeProperty('background-color');
}

/**
 * Bloqueia dark forçado só numa região (ex.: caixa do QR).
 * Não mexe no documento inteiro — o resto da página pode continuar em dark mode.
 */
export function useForcedDarkLockRegion(
  enabled: boolean,
  targetRef: RefObject<HTMLElement | null>
): void {
  useEffect(() => {
    if (!enabled) {
      const root = targetRef.current;
      if (root) {
        clearRegionLock(root);
      }
      if (!document.querySelector(`.${REGION_LOCK_CLASS}`)) {
        document.getElementById(REGION_GUARD_ID)?.remove();
      }
      return;
    }

    let applying = false;
    let observedRoot: HTMLElement | null = null;
    let subtreeObserver: MutationObserver | null = null;

    const apply = () => {
      const root = targetRef.current;
      if (!root || applying) {
        return;
      }
      applying = true;
      try {
        ensureRegionGuardStylesheet();
        applyRegionLock(root);

        if (observedRoot !== root) {
          subtreeObserver?.disconnect();
          observedRoot = root;
          subtreeObserver = new MutationObserver(() => apply());
          subtreeObserver.observe(root, {
            attributes: true,
            subtree: true,
            attributeFilter: ['style', 'class'],
          });
        }
      } finally {
        requestAnimationFrame(() => {
          applying = false;
        });
      }
    };

    apply();

    const docObserver = new MutationObserver(() => apply());
    docObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style', 'class', 'data-darkreader-mode', 'data-darkreader-scheme'],
    });
    if (document.body) {
      docObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['style', 'class'],
      });
    }

    const t1 = window.setTimeout(apply, 50);
    const t2 = window.setTimeout(apply, 250);
    const t3 = window.setTimeout(apply, 1000);

    return () => {
      docObserver.disconnect();
      subtreeObserver?.disconnect();
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);

      const root = targetRef.current;
      if (root) {
        clearRegionLock(root);
      }
      if (!document.querySelector(`.${REGION_LOCK_CLASS}`)) {
        document.getElementById(REGION_GUARD_ID)?.remove();
      }
    };
  }, [enabled, targetRef]);
}
